import { useEffect, useState } from "react";
import { NameIdType } from "../../../assets/utils/enums/project";
import { useFormValidation } from "../../../assets/utils/hooks/useFormsValidation";
import { useNavigate } from "react-router-dom";
import getData from "../../../api/project-request";
import {
  API_URL_FINANCE,
  API_URL_PROJECT,
  API_URL_SELLING,
} from "../../../constants";
import { ValidationFormsModeEnum } from "../../../assets/utils/validation/form/types";
import { authFetch } from "../../../api/authFetch";
import CustomToast from "../../../components/ui/custom-toast";
import toast from "react-hot-toast";
import { ToastTypeEnum } from "../../../assets/utils/enums/toast";
import AppLayout, { MainBody } from "../../../components/layout/app/layout";
import Headline from "../../../components/ui/headline";
import { HeadlineTypeEnum } from "../../../assets/utils/enums/headline";
import { ThemeTypeEnum } from "../../../assets/utils/validation/types";
import { BodyDiv } from "../../../components/components-style";
import { CaptionText, LittleSubHeadline } from "../../../components/typography";
import Input from "../../../components/ui/inputs";
import { DropdownTypeEnum } from "../../../assets/utils/enums/table";
import { Button } from "../../../components/ui/button";
import { COLORS } from "../../../assets/utils/enums/colors";
import { currencies } from "../../../assets/utils/data/items";
import { validateFinance } from "../../../assets/utils/validation/form/validator-finance";
import Dropdown from "../../../components/ui/dropdown";
import { FinanceTypeTable } from "../../../assets/utils/enums/finance";
import ErrorBlock from "../../../components/ui/error-text";
import { findStructureFieldByKey, shouldRenderField, useStructureStore } from "../../../assets/store/useStructureStore";
import { ObjectsEnum } from "../../../assets/utils/enums/basket";

export default function AddIncomePage() {
  const [name, setName] = useState<string>("");
  const [project, setProject] = useState<NameIdType>({ id: "", name: "" });
  const [selling, setSelling] = useState<NameIdType>({ id: "", name: "" });
  const [description, setDescription] = useState<string>("");
  const [budget, setBudget] = useState<string>("");
  const [type, setType] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [filterProjects, setFilterProjects] = useState<NameIdType[]>([]);
  const [filterSellings, setFilterSellings] = useState<NameIdType[]>([]);
  const [currency, setCurrency] = useState<string>(currencies[1].symbol);

  const [projectQuery, setProjectQuery] = useState<string>("");
  const [sellingQuery, setSellingQuery] = useState<string>("");

  const { errors, validate } = useFormValidation(validateFinance);
  const navigate = useNavigate();

  const fields = useStructureStore(
    (s) => s.structures[ObjectsEnum.INCOME].fields
  )

  useEffect(() => {
    if (projectQuery !== project.name) {
      setProject({ id: "", name: "" });
    }

    const getProjects = async () => {
      try {
        const data = await getData(
          `${API_URL_PROJECT}/get-projects?query=${projectQuery}&sellingId=${selling.id}`
        );
        setFilterProjects(Array.isArray(data) ? data.slice(0, 3) : []);

        if (data.length === 0) return;

        if (projectQuery.toLowerCase() !== data[0].name.toLowerCase()) {
          return;
        }
        if (selling.id !== "" && data.length !== 0) {
          setProject({ id: data[0].id, name: data[0].name });
          setProjectQuery(data[0].name);
        }

      } catch {
        setFilterProjects([]);
      }
    };

    getProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectQuery, selling.id]);

  useEffect(() => {
    setIsLoading(true);
    if (sellingQuery !== selling.name) {
      setSelling({ id: "", name: "" });
    }
    const getSellings = async () => {
      try {
        const data = await getData(
          `${API_URL_SELLING}/search-by?query=${sellingQuery}&projectId=${project.id}`
        );
        setFilterSellings(Array.isArray(data) ? data.slice(0, 3) : []);
      } catch {
        setFilterSellings([]);
      } finally {
        setIsLoading(false);
      }
    };

    getSellings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sellingQuery, project.id]);

  const handleSave = async () => {
    try {
      setIsLoading(true);

      const { hasErrors } = validate(
        {
          name,
          type,
          budget,
          project,
          selling,
          description,
        },
        ValidationFormsModeEnum.ADD
      );

      if (hasErrors) return;

      // "name": "Отримали передоплату за послугу",
      // "amount": 500,
      // "valutaCode": "USD",
      // "description": "Отримали передоплату за реалізацію блоку авторизації на сайті",
      // "projectId": 70,
      // "typeName": "Передоплата",
      // "sellingId": null

      const response = await authFetch(`${API_URL_FINANCE}/create-income`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name,
          amount: budget,
          valutaCode:
            currencies.find((x) => x.symbol === currency)?.code || "UAH",
          description: description,
          typeName: type,
          projectId: project.name === "" || !project?.id ? null : project.id,
          sellingId: selling.name === "" || !selling?.id ? null : selling.id,
        }),
        credentials: "include",
      });

      if (!response.ok) {
        toast((t) => (
          <CustomToast
            t={t}
            type={ToastTypeEnum.WARNING}
            text={"Перевірте коректність введення даних!"}
          />
        ));
        return;
      }
      toast((t) => (
        <CustomToast
          t={t}
          type={ToastTypeEnum.SUCCESS}
          text={"Дані введені коректно, завдання додано!"}
        />
      ));
      setTimeout(() => {
        navigate("/finance/income/main");
      }, 300);
    } catch (error) {
      console.error("Fetch error:", error);
      toast((t) => (
        <CustomToast
          t={t}
          text={"Упс, щось пішло не так\nПроблема з сервером."}
          type={ToastTypeEnum.ERROR}
        />
      ));
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 150);
    }
  };

  return (
    <AppLayout
      className="d-flex flex-column align-items-center"
      blockType={"item-form"}
      loaderState={isLoading}
    >
      <Headline
        headline={HeadlineTypeEnum.FINANCE}
        theme={ThemeTypeEnum.LIGHT}
        btn_text="Повернутися до Надходжень"
        btn_link="/finance/income/main"
      />
      <MainBody className="align-items-stretch">
        <BodyDiv
          data-class="add-block"
          className="d-flex flex-column align-items-center"
        >
          <CaptionText>Основні дані про надходження</CaptionText>
          <div className="w-100">
            {shouldRenderField(findStructureFieldByKey(fields, "name")) && <Input
              attribute="nameOfFinance"
              value={name}
              setValue={setName}
              errorText={errors.name}
              label={findStructureFieldByKey(fields, "name")?.label ?? "Назва"}
              placeholder="Введіть назву"
            />}
            {shouldRenderField(findStructureFieldByKey(fields, "project")) && <Input
              attribute={DropdownTypeEnum.PROJECTS}
              item={project}
              setItem={setProject}
              value={projectQuery}
              setValue={setProjectQuery}
              errorText={errors.project}
              itemsNameIdArr={filterProjects}
              label={findStructureFieldByKey(fields, "project")?.label ?? "Проєкт"}
              placeholder="Введіть назву"
            />}

            {shouldRenderField(findStructureFieldByKey(fields, "selling")) && <Input
              attribute={DropdownTypeEnum.SELLINGS}
              item={selling}
              setItem={setSelling}
              value={sellingQuery}
              setValue={setSellingQuery}
              errorText={errors.selling}
              itemsNameIdArr={filterSellings}
              label={findStructureFieldByKey(fields, "selling")?.label ?? "Продажі"}
              placeholder="Введіть назву"
            />}
          </div>
        </BodyDiv>
        <BodyDiv data-class="add-block-other">
          {shouldRenderField(findStructureFieldByKey(fields, "amount")) && <span className="w-100 d-flex flex-column align-items-center">
            <CaptionText>{findStructureFieldByKey(fields, "amount")?.label ?? "Сума"}</CaptionText>
            <Input
              attribute="budget"
              value={budget}
              setValue={setBudget}
              errorText={errors.budget}
              currency={currency}
              setCurrency={setCurrency}
              label=""
              placeholder="Введіть значення"
            />
          </span>}
          {shouldRenderField(findStructureFieldByKey(fields, "typeName")) && <span className="w-100 d-flex flex-column">
            <CaptionText>{findStructureFieldByKey(fields, "typeName")?.label ?? "Тип витрати"}</CaptionText>
            <Dropdown
              type={DropdownTypeEnum.FINANCE_TYPES}
              checkedValue={type}
              setChecked={setType}
              financeTableType={FinanceTypeTable.INCOME}
              placeholder="Виберіть тип"
            />
            {errors.type && <ErrorBlock errorText={errors.type} />}
          </span>}
          {shouldRenderField(findStructureFieldByKey(fields, "comment")) && <span className="w-100 d-flex flex-column align-items-center">
            <CaptionText>{findStructureFieldByKey(fields, "comment")?.label ?? "Опис"}</CaptionText>
            <Input
              attribute="description"
              value={description}
              setValue={setDescription}
              errorText={""}
              label=""
              placeholder="Введіть опис"
            />
          </span>}
        </BodyDiv>
      </MainBody>

      <Button
        bgcolor={COLORS.secondary1}
        txtcolor={COLORS.white}
        brcolor={"transparent"}
        width="30%"
        className="center-btn"
        brradius="24px"
        type="large"
        onClick={handleSave}
      >
        <LittleSubHeadline>Зберегти зміни</LittleSubHeadline>
      </Button>
    </AppLayout>
  );
}
